import { extends as _extends, objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useRef, useState, useEffect, useCallback, cloneElement } from 'react';
import { isString, isArray } from '../../utils/assertion.esm.js';
import '../../utils/ssr.esm.js';
import '../../utils/number.esm.js';
import { removeItem, addItem, getNextItemFromSearch, getNextIndex, getPrevIndex } from '../../utils/array.esm.js';
import '../../utils/breakpoint.esm.js';
import { createContext } from '../../utils/context.esm.js';
import { dataAttr } from '../../utils/dom.esm.js';
import { ensureFocus } from '../../utils/tabbable.esm.js';
import '../../utils/dom-query.esm.js';
import '../../utils/logger.esm.js';
import '../../utils/focus.esm.js';
import { callAllHandlers } from '../../utils/function.esm.js';
import { createOnKeyDown } from '../../utils/keydown.esm.js';
import '../../utils/lazy.esm.js';
import '../../utils/object.esm.js';
import { mergeRefs, cleanChildren } from '../../utils/react-helpers.esm.js';
import '../../utils/style-props.esm.js';
import '../../utils/tailwind.esm.js';
import '../../hooks/use-safe-layout-effects.esm.js';
import '../../hooks/use-callback-ref.esm.js';
import { useClickable } from '../../hooks/use-clickable.esm.js';
import '../../hooks/use-clipboard.esm.js';
import '../../hooks/use-combobox-dropdown-style.esm.js';
import '../../hooks/use-combobox-style.esm.js';
import { useControllableState } from '../../hooks/use-controllable.esm.js';
import '../../hooks/use-local-storage.esm.js';
import '../../hooks/use-media.esm.js';
import '../../hooks/use-prefers-dark-mode.esm.js';
import '../../hooks/use-dark-mode.esm.js';
import '../../hooks/use-force-update.esm.js';
import { useDescendants, useDescendant } from '../../hooks/use-descendant.esm.js';
import { useIds, useId } from '../../hooks/use-id.esm.js';
import '../../hooks/use-previous.esm.js';
import { useDisclosure } from '../../hooks/use-disclosure.esm.js';
import '../../hooks/use-dropdown-style.esm.js';
import '../../hooks/use-latest-ref.esm.js';
import '../../hooks/use-event-listener.esm.js';
import '../../hooks/use-event-listener-v2.esm.js';
import { useUpdateEffect } from '../../hooks/use-update-effect.esm.js';
import '../../hooks/use-focus-on-hide.esm.js';
import '../../hooks/use-pointer-event.esm.js';
import '../../hooks/use-focus-on-pointerdown.esm.js';
import '../../hooks/use-focus-on-show.esm.js';
import '../../hooks/use-fork-ref.esm.js';
import '../../hooks/use-hints.esm.js';
import '../../hooks/use-input-style.esm.js';
import '../../hooks/use-key-press.esm.js';
import '../../hooks/use-lock-body-scroll.esm.js';
import '../../hooks/use-platform.esm.js';
import { useShortcut } from '../../hooks/use-shortcut.esm.js';
import '../Popper/popper.modifiers.esm.js';
import { usePopper } from '../Popper/use-popper.esm.js';
import '../Popper/use-popper-v2.esm.js';
import { useInteractOutside } from '@react-aria/interactions';

var _createContext = /*#__PURE__*/createContext({
  strict: false,
  name: 'MenuContext'
}),
    MenuProvider = _createContext[0],
    useMenuContext = _createContext[1];
function useMenu(props) {
  var id = props.id,
      _props$closeOnSelect = props.closeOnSelect,
      closeOnSelect = _props$closeOnSelect === void 0 ? true : _props$closeOnSelect,
      _props$closeOnClick = props.closeOnClick,
      closeOnClick = _props$closeOnClick === void 0 ? true : _props$closeOnClick,
      _props$closeOnBlur = props.closeOnBlur,
      closeOnBlur = _props$closeOnBlur === void 0 ? true : _props$closeOnBlur,
      _props$autoSelect = props.autoSelect,
      autoSelect = _props$autoSelect === void 0 ? true : _props$autoSelect,
      _props$placement = props.placement,
      placementProp = _props$placement === void 0 ? 'bottom-start' : _props$placement,
      _props$gutter = props.gutter,
      gutter = _props$gutter === void 0 ? 8 : _props$gutter,
      _props$fixed = props.fixed,
      fixed = _props$fixed === void 0 ? false : _props$fixed,
      preventOverflow = props.preventOverflow; // Regular open and close stuff

  var _useDisclosure = useDisclosure(),
      open = _useDisclosure.open,
      onOpen = _useDisclosure.onOpen,
      onClose = _useDisclosure.onClose,
      onToggle = _useDisclosure.onToggle; // Prepare the reference to the menu and disclosure


  var menuRef = useRef(null);
  var buttonRef = useRef(null);
  useInteractOutside({
    ref: menuRef,
    onInteractOutside: function onInteractOutside(event) {
      var _buttonRef$current;

      if (closeOnBlur && !((_buttonRef$current = buttonRef.current) == null ? void 0 : _buttonRef$current.contains(event.target))) {
        onClose();
      }
    }
  }); // Add some popper.js for dynamic positioning

  var _usePopper = usePopper({
    placement: placementProp,
    fixed: fixed,
    forceUpdate: open,
    gutter: gutter,
    skidding: 0,
    preventOverflow: preventOverflow
  }),
      placement = _usePopper.placement,
      popper = _usePopper.popper,
      reference = _usePopper.reference;

  var _React$useState = useState(-1),
      focusedIndex = _React$useState[0],
      setFocusedIndex = _React$useState[1]; // Context to register all menu item nodes


  var domContext = useDescendants(); // Focus the top-level disclosure when we close the menu

  useUpdateEffect(function () {
    if (!open) {
      if (buttonRef.current) {
        ensureFocus(buttonRef.current);
      }
    }
  }, [open]); // Reset the focused index if the menu is closed

  useEffect(function () {
    if (!open) {
      setFocusedIndex(-1);
    }
  }, [open]); // Generate unique ids for menu's list and button

  var _useIds = useIds(id, 'menu-button', 'menu-list'),
      buttonId = _useIds[0],
      menuId = _useIds[1];

  return {
    domContext: domContext,
    popper: popper,
    placement: placement,
    reference: reference,
    buttonId: buttonId,
    menuId: menuId,
    orientation: 'vertical',
    open: open,
    onToggle: onToggle,
    onOpen: onOpen,
    onClose: onClose,
    menuRef: menuRef,
    buttonRef: buttonRef,
    focusedIndex: focusedIndex,
    closeOnSelect: closeOnSelect,
    closeOnClick: closeOnClick,
    closeOnBlur: closeOnBlur,
    autoSelect: autoSelect,
    setFocusedIndex: setFocusedIndex
  };
} // FIXME: The return type is a bodge to prevent TS2742: The inferred type of 'useMenuList' cannot be named without a reference to 'react/node_modules/csstype'. This is likely not portable. A type annotation is necessary.
// eslint-disable-next-line @typescript-eslint/no-explicit-any

function useMenuList(props) {
  var menu = useMenuContext();
  var focusedIndex = menu.focusedIndex,
      setFocusedIndex = menu.setFocusedIndex,
      menuRef = menu.menuRef,
      open = menu.open,
      onClose = menu.onClose,
      popper = menu.popper,
      menuId = menu.menuId,
      placement = menu.placement,
      descendants = menu.domContext.descendants; // Hook that creates a keydown event handler that listens
  // to printable keyboard character press

  var onCharacterPress = useShortcut({
    preventDefault: function preventDefault(event) {
      return event.key !== ' ';
    }
  });
  var onKeyDown = createOnKeyDown({
    onKeyDown: onCharacterPress(function (character) {
      // Typeahead: Based on current character pressed,
      // find the next item to be selected
      var nextItem = getNextItemFromSearch(descendants, character, function (node) {
        var _node$element;

        return ((_node$element = node.element) == null ? void 0 : _node$element.textContent) || '';
      }, descendants[focusedIndex]);

      if (nextItem) {
        var index = descendants.indexOf(nextItem);
        setFocusedIndex(index);
      }
    }),
    keyMap: {
      Escape: onClose,
      ArrowDown: function ArrowDown() {
        var nextIndex = getNextIndex(focusedIndex, descendants.length);
        setFocusedIndex(nextIndex);
      },
      ArrowUp: function ArrowUp() {
        var prevIndex = getPrevIndex(focusedIndex, descendants.length);
        setFocusedIndex(prevIndex);
      }
    }
  });
  return _extends({}, props, {
    ref: mergeRefs(menuRef, popper.ref),
    tabIndex: -1,
    role: 'menu',
    id: menuId,
    open: open,
    'aria-orientation': 'vertical',
    'data-placement': placement,
    placement: placement,
    style: _extends({}, props.style, popper.style),
    onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown)
  });
}
function useMenuButton(props) {
  var menu = useMenuContext();
  var setFocusedIndex = menu.setFocusedIndex,
      onOpen = menu.onOpen,
      open = menu.open,
      onClose = menu.onClose,
      autoSelect = menu.autoSelect,
      menuRef = menu.menuRef,
      descendants = menu.domContext.descendants;
  var openAndFocusMenu = useCallback(function () {
    onOpen();

    if (menuRef.current) {
      ensureFocus(menuRef.current);
    }
  }, [onOpen, menuRef]);
  var openAndFocusFirstItem = useCallback(function () {
    onOpen();
    setFocusedIndex(0);
  }, [onOpen, setFocusedIndex]);
  var openAndFocusLastItem = useCallback(function () {
    onOpen();
    var lastIndex = descendants.length - 1;
    setFocusedIndex(lastIndex);
  }, [onOpen, setFocusedIndex, descendants]); // Click handler for the top-level menu button

  var onClick = useCallback(function () {
    if (open) {
      onClose();
    }

    if (!open) {
      if (autoSelect) {
        openAndFocusFirstItem();
      } else {
        openAndFocusMenu();
      }
    }
  }, [autoSelect, open, onClose, openAndFocusFirstItem, openAndFocusMenu]);
  var onKeyDown = createOnKeyDown({
    keyMap: {
      Enter: openAndFocusFirstItem,
      ArrowDown: openAndFocusFirstItem,
      ArrowUp: openAndFocusLastItem
    }
  });
  return _extends({}, props, {
    ref: mergeRefs(menu.buttonRef, menu.reference.ref),
    id: menu.buttonId,
    active: menu.open,
    'data-active': dataAttr(menu.open),
    'aria-expanded': menu.open,
    'aria-haspopup': 'menu',
    'aria-controls': menu.menuId,
    onClick: callAllHandlers(props.onClick, onClick),
    onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown)
  });
}
function useMenuItem(props) {
  var menu = useMenuContext();

  var onClickProp = props.onClick,
      disabled = props.disabled,
      focusable = props.focusable,
      rest = _objectWithoutPropertiesLoose(props, ["onMouseOut", "onClick", "disabled", "focusable"]);

  var domContext = menu.domContext,
      setFocusedIndex = menu.setFocusedIndex,
      focusedIndex = menu.focusedIndex,
      closeOnSelect = menu.closeOnSelect,
      closeOnClick = menu.closeOnClick,
      onClose = menu.onClose,
      menuRef = menu.menuRef;
  var ref = useRef(null);
  var id = "menuitem-" + useId(); // Register the menuitem's node into the domContext

  var index = useDescendant({
    element: ref.current,
    context: domContext,
    disabled: disabled,
    focusable: focusable
  });
  var onMouseEnter = useCallback(function () {
    if (disabled) return;
    setFocusedIndex(index);
  }, [setFocusedIndex, index, disabled]);
  var onMouseMove = useCallback(function () {
    if (document.activeElement !== ref.current) {
      onMouseEnter();
    }
  }, [onMouseEnter]);
  var onMouseLeave = useCallback(function () {
    if (disabled) return;
    setFocusedIndex(-1);
  }, [setFocusedIndex, disabled]);
  var onClick = useCallback(function (event) {
    var target = event.currentTarget;
    var role = target.getAttribute('role'); // If we're clicking on an menuitem that's a menu-button
    // for a submenu ignore the click

    if (target.hasAttribute('aria-controls')) {
      event.preventDefault();
      return;
    }

    onClickProp == null ? void 0 : onClickProp(event); // Close menu and parent menu's if either `closeOnClick` or `closeOnSelect` (depending on the target) are set to `true`

    if (role === 'menuitemradio' || role === 'menuitemcheckbox') {
      if (closeOnSelect) {
        onClose();
      }
    } else if (closeOnClick) {
      onClose();
    }
  }, [onClose, onClickProp, closeOnClick]);
  var isFocused = index === focusedIndex;
  var trulyDisabled = disabled && !focusable;
  useUpdateEffect(function () {
    if (isFocused && !trulyDisabled) {
      if (ref.current) {
        ensureFocus(ref.current);
      }
    } else if (document.activeElement !== menuRef.current) {
      var _menuRef$current;

      (_menuRef$current = menuRef.current) == null ? void 0 : _menuRef$current.focus();
    }
  }, [isFocused, trulyDisabled, menuRef]);
  var tabbable = useClickable({
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseMove: onMouseMove,
    onMouseLeave: onMouseLeave,
    ref: ref,
    disabled: disabled,
    focusable: focusable
  });
  var styles = {
    fullWidth: true,
    group: 'group',
    appearance: 'ghost',
    padding: ['px-3', 'py-1'],
    justifyContent: 'justify-start',
    fontWeight: 'font-normal',
    lineHeight: 'leading-normal',
    backgroundColor: !disabled ? ['hover:bg-blue-500', 'focus:bg-blue-500'] : undefined,
    textColor: disabled ? 'text-gray-400' : ['text-gray-500', 'hover:text-white', 'focus:text-white'],
    transitionDuration: 'duration-75',
    textAlign: 'text-left'
  };
  return _extends({}, rest, tabbable, styles, {
    id: id,
    role: 'menuitem',
    tabIndex: isFocused ? 0 : -1
  });
}
function useMenuOption(props) {
  var onClick = props.onClick,
      disabled = props.disabled,
      focusable = props.focusable,
      _props$type = props.type,
      type = _props$type === void 0 ? 'radio' : _props$type,
      checked = props.checked,
      rest = _objectWithoutPropertiesLoose(props, ["onMouseOut", "onClick", "disabled", "focusable", "type", "checked"]);

  var ownProps = useMenuItem({
    disabled: disabled,
    focusable: focusable,
    onClick: onClick
  });
  return _extends({}, rest, ownProps, {
    role: "menuitem" + type,
    'aria-checked': checked
  });
}
function useMenuOptionGroup(props) {
  var children = props.children,
      _props$type2 = props.type,
      type = _props$type2 === void 0 ? 'radio' : _props$type2,
      valueProp = props.value,
      defaultValue = props.defaultValue,
      onChange = props.onChange,
      rest = _objectWithoutPropertiesLoose(props, ["children", "type", "value", "defaultValue", "onChange"]);

  var isRadio = type === 'radio';
  var fallback = isRadio ? '' : [];

  var _useControllableState = useControllableState({
    defaultValue: defaultValue != null ? defaultValue : fallback,
    value: valueProp,
    onChange: onChange
  }),
      value = _useControllableState[0],
      setValue = _useControllableState[1];

  var handleChange = useCallback(function (selectedValue) {
    if (type === 'radio' && isString(value)) {
      setValue(selectedValue);
    }

    if (type === 'checkbox' && isArray(value)) {
      var nextValue = value.includes(selectedValue) ? removeItem(value, selectedValue) : addItem(value, selectedValue);
      setValue(nextValue);
    }
  }, [value, setValue, type]);
  var clones = cleanChildren(children).map(function (child) {
    var onClick = function onClick(event) {
      handleChange(child.props.value);
      child.props.onClick == null ? void 0 : child.props.onClick(event);
    };

    var checked = type === 'radio' ? child.props.value === value : value.includes(child.props.value);
    return cloneElement(child, {
      type: type,
      onClick: onClick,
      checked: checked
    });
  });
  return _extends({}, rest, {
    children: clones
  });
}

export { MenuProvider, useMenu, useMenuButton, useMenuContext, useMenuItem, useMenuList, useMenuOption, useMenuOptionGroup };
//# sourceMappingURL=use-menu.esm.js.map
